import React from 'react';
import * as S from './ProductSearchResultTile.style';
import {
    Flex,
    Image,
    Caption,
    useStyles,
    pxToRem,
} from '@archipro-design/aria';
import type {
    ProductTaggingSearchResult,
    TagDataUseToCreateTag,
} from '@modules/product-tagging/type';
import { convertSearchResultToTagType } from '../../util/convert-search-result-to-tag-type';
import { getUsesNeutralMultiplyTreatment } from '../../util/get-uses-neutral-multiply-treatment';
import { needsDefaultBorder } from '@rocco/utils/colour-helpers';
import { ReactComponent as ArchiproSymbolLogo } from '@rocco/icons/svg/archipro-symbol-logo.svg';

interface ProductSearchResultTileProps {
    tileData: ProductTaggingSearchResult;
    onConfirm: (data: TagDataUseToCreateTag) => void;
    showProfessionalName?: boolean;
}

const ProductSearchResultTile = ({
    tileData,
    showProfessionalName,
    onConfirm,
}: ProductSearchResultTileProps) => {
    const logoBackground = tileData.logoBackground;
    const isOffPlatformStub = tileData.pType === 'OffPlatformSupplier';
    const isProfessional =
        tileData.pType === 'Professional' || isOffPlatformStub;
    const usesNeutralMultiply = getUsesNeutralMultiplyTreatment(tileData);
    const useDefaultBorder =
        isProfessional &&
        (isOffPlatformStub || needsDefaultBorder(logoBackground));

    const { css } = useStyles({
        logoBackground,
        isProfessional,
        isOffPlatformStub,
        usesNeutralMultiply,
        useDefaultBorder,
    });

    const data = convertSearchResultToTagType(tileData);
    const stubDomain = tileData.normalisedDomain?.trim();

    return (
        <Flex
            gap={'space-8'}
            className={css(S.ResultTile)}
            onClick={() => onConfirm(data)}
        >
            <div className={css(S.ResultTileThumb)}>
                {isOffPlatformStub ? (
                    <div className={css(S.OffPlatformStubLogoSlot)} aria-hidden>
                        <ArchiproSymbolLogo />
                    </div>
                ) : (
                    <Image
                        src={tileData.thumb}
                        width={isProfessional ? 40 : 48}
                        height={isProfessional ? 40 : 48}
                        layout="fixed"
                        objectFit={'contain'}
                        alt={tileData.title ?? ''}
                        mixBlendMode={
                            usesNeutralMultiply ? 'multiply' : undefined
                        }
                        styles={
                            usesNeutralMultiply
                                ? {
                                      root: {
                                          borderRadius: isProfessional
                                              ? '50%'
                                              : pxToRem(8),
                                          overflow: 'hidden',
                                          background: 'transparent',
                                      },
                                  }
                                : undefined
                        }
                        placeholderColor={
                            isProfessional && !usesNeutralMultiply
                                ? 'transparent'
                                : undefined
                        }
                    />
                )}
            </div>
            <Flex column className={css(S.ResultTileContent)}>
                {isOffPlatformStub && stubDomain ? (
                    <div className={css(S.OffPlatformStubTextStack)}>
                        <Caption
                            variant="04"
                            className={css(S.OffPlatformStubTitle)}
                            data-off-platform-stub-title
                        >
                            {tileData.title}
                        </Caption>
                        <Caption
                            variant="05"
                            className={css(S.OffPlatformStubDomain)}
                            data-off-platform-stub-domain
                        >
                            {stubDomain}
                        </Caption>
                    </div>
                ) : (
                    <>
                        <Caption
                            variant="04"
                            className={css(S.ResultTileTitle)}
                        >
                            {tileData.title}
                        </Caption>
                        {tileData.professional && showProfessionalName && (
                            <Caption
                                variant="04"
                                className={css(S.ResultTileProfessional)}
                            >
                                {tileData.professional}
                            </Caption>
                        )}
                    </>
                )}
            </Flex>
        </Flex>
    );
};

export default ProductSearchResultTile;
